/**
 * Help System Composable
 *
 * Provides state management and navigation for the comprehensive help system.
 * Supports contextual help, search, and navigation history.
 *
 * Usage:
 * ```ts
 * import { useHelp } from '@/composables/useHelp';
 *
 * const { openHelp, closeHelp, navigateTo, searchHelp } = useHelp();
 *
 * // Open help panel at specific article
 * openHelp('boq.add-item');
 *
 * // Search help content
 * searchHelp('calculation');
 * ```
 */

import { ref, computed, readonly } from 'vue';
import { helpRegistry, type HelpArticle } from '@/data/help';

// Global help state (singleton pattern)
const isOpen = ref(false);
const currentArticleId = ref<string | null>(null);
const searchQuery = ref('');
const navigationHistory = ref<string[]>([]);
const tocExpanded = ref<Record<string, boolean>>({});

// Search index cache
let searchIndex: { article: HelpArticle; searchText: string }[] = [];
let searchIndexBuilt = false;

/**
 * Build search index from all help articles
 */
function buildSearchIndex(): void {
  if (searchIndexBuilt) return;

  searchIndex = Object.values(helpRegistry).map(article => ({
    article,
    searchText: [
      article.title,
      article.summary,
      article.content,
      ...(article.keywords || []),
      ...(article.expandableSections?.map(s => `${s.title} ${s.content}`) || []),
    ].join(' ').toLowerCase(),
  }));

  searchIndexBuilt = true;
}

/**
 * Get the current article
 */
const currentArticle = computed((): HelpArticle | null => {
  if (!currentArticleId.value) return null;
  return helpRegistry[currentArticleId.value] || null;
});

/**
 * Get search results
 */
const searchResults = computed((): HelpArticle[] => {
  if (!searchQuery.value || searchQuery.value.length < 2) return [];

  buildSearchIndex();

  const query = searchQuery.value.toLowerCase();
  const terms = query.split(/\s+/).filter(t => t.length > 1);

  if (terms.length === 0) return [];

  // Score articles based on matches
  const scored = searchIndex
    .map(({ article, searchText }) => {
      let score = 0;

      // Title match (highest priority)
      if (article.title.toLowerCase().includes(query)) {
        score += 100;
      }

      // Exact phrase match
      if (searchText.includes(query)) {
        score += 50;
      }

      // Individual term matches
      for (const term of terms) {
        if (article.title.toLowerCase().includes(term)) {
          score += 20;
        }
        if (article.summary.toLowerCase().includes(term)) {
          score += 10;
        }
        if (searchText.includes(term)) {
          score += 5;
        }
        if (article.keywords?.some(k => k.toLowerCase().includes(term))) {
          score += 15;
        }
      }

      return { article, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 20)
    .map(({ article }) => article);

  return scored;
});

/**
 * Get articles grouped by category for TOC
 */
const articlesByCategory = computed((): Record<string, HelpArticle[]> => {
  const grouped: Record<string, HelpArticle[]> = {};

  for (const article of Object.values(helpRegistry)) {
    const category = article.category;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(article);
  }

  // Sort articles within each category
  for (const articles of Object.values(grouped)) {
    articles.sort((a, b) => {
      // Sort by order if available, then by title
      const orderA = a.order ?? 999;
      const orderB = b.order ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return a.title.localeCompare(b.title);
    });
  }

  return grouped;
});

/**
 * Get sorted category names
 */
const categories = computed((): string[] => {
  const categoryOrder = [
    'Getting Started',
    'Dashboard',
    'Jobs',
    'Projects',
    'BOQ',
    'Catalogue',
    'Orders',
    'Suppliers',
    'Contacts',
    'Accounting',
    'Documents',
    'Transmittals',
    'Reports',
    'Recycle Bin',
    'Settings',
    'Features',
  ];

  const cats = Object.keys(articlesByCategory.value);
  cats.sort((a, b) => {
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b);
  });

  return cats;
});

/**
 * Check if we can go back in history
 */
const canGoBack = computed((): boolean => navigationHistory.value.length > 1);

/**
 * Open the help panel
 * @param articleId Optional article ID to navigate to
 */
function openHelp(articleId?: string): void {
  isOpen.value = true;
  if (articleId) {
    navigateTo(articleId);
  } else if (!currentArticleId.value) {
    // Default to getting started if no article selected
    navigateTo('getting-started.welcome');
  }
}

/**
 * Close the help panel
 */
function closeHelp(): void {
  isOpen.value = false;
  searchQuery.value = '';
}

/**
 * Toggle the help panel
 */
function toggleHelp(): void {
  if (isOpen.value) {
    closeHelp();
  } else {
    openHelp();
  }
}

/**
 * Navigate to a specific article
 * @param articleId The article ID to navigate to
 */
function navigateTo(articleId: string): void {
  const article = helpRegistry[articleId];
  if (!article) {
    console.warn(`Help article not found: ${articleId}`);
    return;
  }

  // Add to history only if different from current
  if (currentArticleId.value !== articleId) {
    if (currentArticleId.value) {
      navigationHistory.value.push(currentArticleId.value);
      // Limit history size
      if (navigationHistory.value.length > 50) {
        navigationHistory.value.shift();
      }
    }
    currentArticleId.value = articleId;
  }

  // Clear search when navigating
  searchQuery.value = '';

  // Expand the category in TOC
  tocExpanded.value[article.category] = true;
}

/**
 * Go back in navigation history
 */
function goBack(): void {
  if (!canGoBack.value) return;

  const previousId = navigationHistory.value.pop();
  if (previousId) {
    currentArticleId.value = previousId;
    searchQuery.value = '';
  }
}

/**
 * Set the search query
 * @param query Search query string
 */
function setSearchQuery(query: string): void {
  searchQuery.value = query;
}

/**
 * Clear search
 */
function clearSearch(): void {
  searchQuery.value = '';
}

/**
 * Toggle TOC category expansion
 * @param category Category name
 */
function toggleTocCategory(category: string): void {
  tocExpanded.value[category] = !tocExpanded.value[category];
}

/**
 * Get contextual article ID based on current route
 * @param route Vue Router route object
 */
function getContextualArticleId(routePath: string): string | null {
  // Derive the mapping from the registry itself: every article declares the
  // route it documents, so new categories (Timesheets, Quotes, …) resolve
  // without touching this file. Longest route match wins (most specific),
  // then lowest order — i.e. that category's overview article.
  const candidates = Object.values(helpRegistry)
    .filter(a => a.route && (routePath === a.route || routePath.startsWith(a.route + '/')))
    .sort(
      (a, b) =>
        b.route!.length - a.route!.length || (a.order ?? 999) - (b.order ?? 999)
    );
  if (candidates.length > 0) return candidates[0].id;

  // Legacy fallback for any route whose articles lack a `route` field, plus
  // pages documented by an article that lives on a sibling route.
  const routeMap: Record<string, string> = {
    '/report-templates': 'report-designer.overview',
    '/dashboard': 'dashboard.overview',
    '/jobs': 'jobs.overview',
    '/projects': 'projects.overview',
    '/boq': 'boq.overview',
    '/catalogue': 'catalogue.overview',
    '/orders': 'orders.overview',
    '/suppliers': 'suppliers.overview',
    '/contacts': 'contacts.overview',
    '/accounting': 'accounting.overview',
    '/documents': 'documents.overview',
    '/transmittals': 'transmittals.overview',
    '/reports': 'reports.overview',
    '/rfqs': 'rfq.overview',
    '/recycle-bin': 'recycle-bin.overview',
    '/settings': 'settings.overview',
    '/admin': 'admin.overview',
  };

  return routeMap[routePath] || 'getting-started.welcome';
}

/**
 * Open contextual help for current route
 */
function openContextualHelp(): void {
  // Use window.location.pathname since this may be called outside component setup
  const articleId = getContextualArticleId(window.location.pathname);
  openHelp(articleId || undefined);
}

/**
 * Get related articles for current article
 */
const relatedArticles = computed((): HelpArticle[] => {
  if (!currentArticle.value?.relatedArticles) return [];

  return currentArticle.value.relatedArticles
    .map(id => helpRegistry[id])
    .filter(Boolean) as HelpArticle[];
});

/**
 * Help system composable
 */
export function useHelp() {
  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    currentArticleId: readonly(currentArticleId),
    currentArticle,
    searchQuery: readonly(searchQuery),
    searchResults,
    navigationHistory: readonly(navigationHistory),
    canGoBack,
    articlesByCategory,
    categories,
    tocExpanded,
    relatedArticles,

    // Actions
    openHelp,
    closeHelp,
    toggleHelp,
    navigateTo,
    goBack,
    setSearchQuery,
    clearSearch,
    toggleTocCategory,
    getContextualArticleId,
    openContextualHelp,
  };
}

// Export singleton instance for direct import
export const help = useHelp();
